import { usePageMeta } from './PageMetaContext.jsx';
import { wrapGlossaryTerms } from '../components/primitives/GlossaryTerm.jsx';
import { AboutThisPageButton } from '../features/help/AboutThisPageButton.jsx';
import './pageHeader.css';

// Shell-level page header (PRIME-SHELL-UX-001).
//
// This establishes the SINGLE visible page-identity block now that the topbar
// no longer renders a route title/subtitle. It renders exactly one <h1>, an
// optional eyebrow, an optional plain-language purpose line (05 §2/§6 page
// contract — jargon inside it still receives shared-glossary hover
// definitions), and an actions row that mounts the relocated "About this page"
// control beside the title by default.
//
// With zero props it reads sensible defaults from the active page metadata —
// the SAME PageMetaContext the topbar reads — so a simple route can adopt the
// header with just `<PageHeader />`. Callers may override any field or pass
// additional `actions`; the About control still renders unless `showAbout` is
// false.
//
// It intentionally reuses the shared `.page-header` / `.page-title` /
// `.page-subtitle` / `.eyebrow` visual language (styles/app.css) so adopted
// routes look identical to pages that use the lower-level PageHeader primitive.
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  children,
  showAbout = true,
}) {
  const { meta } = usePageMeta();
  const resolvedTitle = title ?? meta?.title ?? '';
  const resolvedSubtitle = subtitle ?? meta?.subtitle ?? '';
  const resolvedEyebrow = eyebrow ?? meta?.kicker ?? null;
  const hasActions = Boolean(actions) || showAbout;

  return (
    <header className="page-header shell-page-header" data-testid="shell-page-header">
      <div className="shell-page-header__text">
        {resolvedEyebrow ? <div className="eyebrow">{resolvedEyebrow}</div> : null}
        {resolvedTitle ? <h1 className="page-title">{resolvedTitle}</h1> : null}
        {resolvedSubtitle ? (
          <p className="page-subtitle" data-testid="shell-page-header-purpose">
            {wrapGlossaryTerms(resolvedSubtitle)}
          </p>
        ) : null}
        {children ? <div className="page-header-extra">{children}</div> : null}
      </div>
      {hasActions ? (
        <div className="page-header-actions shell-page-header__actions">
          {actions}
          {showAbout ? <AboutThisPageButton /> : null}
        </div>
      ) : null}
    </header>
  );
}
